import { lazy, Suspense } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Services from "@/components/Services";
import StickyCallBar from "@/components/StickyCallBar";

// Below-the-fold sections lazy-loaded for faster mobile TTI + smaller initial bundle
const JunkRemoval = lazy(() => import("@/components/JunkRemoval"));
const Gallery = lazy(() => import("@/components/Gallery"));
const Equipment = lazy(() => import("@/components/Equipment"));
const SnowPackages = lazy(() => import("@/components/SnowPackages"));
const AddOns = lazy(() => import("@/components/AddOns"));
const AreaAndTrust = lazy(() => import("@/components/AreaAndTrust"));
const QuoteForm = lazy(() => import("@/components/QuoteForm"));
const Footer = lazy(() => import("@/components/Footer"));

const SectionFallback = () => (
  <div className="min-h-[300px] w-full" aria-hidden="true" />
);

export default function Landing() {
  const scrollToQuote = () => {
    const el = document.getElementById("quote");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="landing-page" className="bg-[#0a1210] text-white overflow-x-hidden">
      <Header onQuoteClick={scrollToQuote} />
      <main>
        <Hero onQuoteClick={scrollToQuote} />
        <Services onQuoteClick={scrollToQuote} />
        <Suspense fallback={<SectionFallback />}>
          <JunkRemoval onQuoteClick={scrollToQuote} />
          <Gallery onQuoteClick={scrollToQuote} />
          <Equipment />
          <PricingSection onQuoteClick={scrollToQuote} />
          <AddOns onQuoteClick={scrollToQuote} />
          <SnowPackages onQuoteClick={scrollToQuote} />
          <AreaAndTrust />
          <QuoteForm />
        </Suspense>
      </main>
      <Suspense fallback={<SectionFallback />}>
        <Footer />
      </Suspense>
      <StickyCallBar />
    </div>
  );
}

function PricingSection({ onQuoteClick }) {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 lg:py-32 border-t border-white/5 bg-[#0d1614]"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <span className="label-eyebrow">— Standard package</span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1]">
            Lawn care, on
            <br />
            <span className="text-[#8AB273]">auto-pilot.</span>
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-md">
            Our most popular monthly plan handles the recurring lawn
            maintenance most Niagara properties need — and locks in priority
            scheduling for add-ons like trimming, junk haul-away, and snow
            service.
          </p>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5 lg:gap-6">
          {/* Featured plan */}
          <div className="sm:col-span-2 relative overflow-hidden rounded-3xl p-8 lg:p-10 border border-[#8AB273]/40 bg-gradient-to-br from-[#172621] to-[#0a1210]">
            <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-[#8AB273]/15 blur-3xl" />
            <div className="relative">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="label-eyebrow">Monthly Packages Starting At · Most popular</div>
                <span className="text-xs uppercase tracking-[0.18em] text-white/45">
                  Recurring service
                </span>
              </div>
              <div className="mt-4 flex items-baseline gap-3">
                <span className="font-display text-7xl lg:text-8xl font-extrabold tracking-tighter">
                  $135
                </span>
                <span className="text-white/55">+ tax</span>
              </div>
              <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3 max-w-xl">
                {[
                  "Recurring mow, edge & blow",
                  "Seasonal trimming touch-ups",
                  "Same crew, every visit",
                  "Priority booking for add-ons",
                  "Cancel or pause anytime",
                  "Niagara-Region-only crews",
                ].map((b) => (
                  <div key={b} className="flex gap-2 text-sm text-white/80">
                    <span className="text-[#8AB273]">✓</span>
                    {b}
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  data-testid="pricing-monthly-cta"
                  onClick={onQuoteClick}
                  className="bg-[#8AB273] text-black font-semibold px-6 py-3.5 rounded-full hover:bg-[#9DC585]"
                >
                  Start monthly plan
                </button>
                <button
                  onClick={onQuoteClick}
                  data-testid="pricing-custom-cta"
                  className="ppc-glass rounded-full px-6 py-3.5 text-white/90 hover:bg-white/5"
                >
                  Custom quote
                </button>
              </div>
            </div>
          </div>

          <SmallPlan
            title="One-time services"
            blurb="Junk removal, makeovers, patio work and tree jobs priced per project."
            testId="plan-onetime"
          />
          <SmallPlan
            title="Add-on services"
            blurb="Edging, hedge & tree trimming, mulching, aeration, bin washing and more — stack them on your monthly."
            testId="plan-addons-link"
          />
        </div>
      </div>
    </section>
  );
}

function SmallPlan({ title, blurb, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-3xl p-7 border border-white/8 bg-[#131c17] hover:-translate-y-1 transition-all"
    >
      <div className="label-eyebrow text-white/50">Custom</div>
      <h3 className="font-display mt-3 text-2xl font-bold tracking-tight">
        {title}
      </h3>
      <p className="text-sm text-white/55 mt-2 leading-relaxed">{blurb}</p>
      <div className="mt-5 text-sm text-[#8AB273]">Quote on request →</div>
    </div>
  );
}
